import React, { useEffect, useState } from "react";
import { FaCopy, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import axiosInstance from "./api/axiosInstance";
import PasswordInput from "./PasswordInput";
import "../assets/css/SocialPasswordReveal.css";

const HIDE_AFTER_MS = 60000;

function errorText(err) {
  return err?.response?.data?.message || err?.response?.data?.error || "Could not show this value.";
}

const pickSaved = (res) => res.data?.data?.Password ?? res.data?.data?.Value;

function CopyValue({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {
      /* clipboard blocked */
    }
  };
  return (
    <span className="spr__row">
      {label ? <span className="spr__label">{label}</span> : null}
      <code className="spr__value">{value || "—"}</code>
      {value ? (
        <button type="button" className="spr__icon" title={copied ? "Copied" : "Copy"} onClick={copy}>
          <FaCopy aria-hidden="true" />
        </button>
      ) : null}
      {copied ? <span className="spr__note">Copied</span> : null}
    </span>
  );
}

/**
 * Saved social account secret (password, or with ``field`` "api_secret" / "access_token"): masked, shown
 * only after the signed-in person re-enters their own login password (checked and logged by the API at
 * ``revealUrl``). Hides itself again after 1 minute. With ``pickUsername`` the username is shown with it.
 */
export default function SocialPasswordReveal({ hasPassword, revealUrl, field, pickPassword = pickSaved, pickUsername }) {
  const [asking, setAsking] = useState(false);
  const [login, setLogin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [plain, setPlain] = useState(null);
  const [username, setUsername] = useState(null);

  useEffect(() => {
    if (plain === null) return undefined;
    const t = setTimeout(() => setPlain(null), HIDE_AFTER_MS);
    return () => clearTimeout(t);
  }, [plain]);

  if (!hasPassword) return <span className="spr">—</span>;

  const cancel = () => {
    setAsking(false);
    setLogin("");
    setError("");
  };

  const submit = async () => {
    if (!login || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await axiosInstance.post(revealUrl, field ? { confirm_password: login, field } : { confirm_password: login });
      setPlain(pickPassword(res) ?? "");
      setUsername(pickUsername ? pickUsername(res) ?? "" : null);
      cancel();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (plain !== null) {
    const hide = (
      <button type="button" className="spr__icon" title="Hide" onClick={() => setPlain(null)}>
        <FaEyeSlash aria-hidden="true" />
      </button>
    );
    if (username !== null) {
      return (
        <span className="spr spr--asking">
          <CopyValue label="Username" value={username} />
          <span className="spr__row">
            <CopyValue label="Password" value={plain} />
            {hide}
          </span>
        </span>
      );
    }
    return (
      <span className="spr">
        <CopyValue value={plain} />
        {hide}
      </span>
    );
  }

  if (asking) {
    return (
      <span className="spr spr--asking">
        <span className="spr__ask">
          <FaLock aria-hidden="true" /> Enter your login password to see it
        </span>
        <span className="spr__row">
          <PasswordInput
            wrapperClassName="spr__input"
            autoFocus
            autoComplete="current-password"
            placeholder="Your login password"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              } else if (e.key === "Escape") {
                cancel();
              }
            }}
          />
          <button type="button" className="spr__btn spr__btn--primary" onClick={submit} disabled={busy || !login}>
            {busy ? "Checking…" : "Show"}
          </button>
          <button type="button" className="spr__btn" onClick={cancel} disabled={busy}>
            Cancel
          </button>
        </span>
        {error ? <span className="spr__error">{error}</span> : null}
      </span>
    );
  }

  return (
    <span className="spr">
      <code className="spr__value">••••••••</code>
      <button type="button" className="spr__btn" onClick={() => setAsking(true)}>
        <FaEye aria-hidden="true" /> {pickUsername ? "Show username & password" : "Reveal"}
      </button>
    </span>
  );
}
